import Notification from "notifications/notification";
import MessageNotification from "notifications/message-notifications/message-notification";
import { Action } from "notifications/notification-action";
import {
  isKnownNotificationAction,
  isSupportedNotificationActionLink,
  parseNotificationActionLink,
} from "notifications/notification-action-link";
import { notificationLinks } from "config";
import TopicCategory from "notifications/topics/topic-category";
import { conversationDestinationPath, isConversationRecord } from "messaging/conversations/conversation-links";

export type NotificationType = Notification & MessageNotification;
export type NotificationRouteState = {
  fromNotification: true;
  returnTo: string;
};

export function NotificationLink(notification: NotificationType | null | undefined): string {
  if (!notification) return "";
  const suppliedContract = notification.actionLink;
  if (suppliedContract === null) return "";
  const actionLink = parseNotificationActionLink(suppliedContract);
  if (suppliedContract !== undefined && !actionLink) return "";
  if (actionLink && !isSupportedNotificationActionLink(actionLink)) return "";
  if (!actionLink && notification.hasUnrecognizedActionRequired) return "";

  const id = actionLink?.topicId ?? notification.topic?.id?.value;
  const category = actionLink?.topicCategory ?? notification.topic?.category ?? "";
  const action = actionLink ? actionLink.action ?? undefined : notification.actionRequired;
  if (action && !isKnownNotificationAction(action)) return "";
  let url = notificationLinks[category as keyof typeof notificationLinks] ?? "";

  if (!id) return url;

  // A new-message notification is topic-associated with the forum the message was posted in
  // (#3144), so its topic id *is* the forum id and the ordinary `Messaging.Forum` route template
  // resolves it like any other category. Nothing here reads `messageInfo`, which means the deep
  // link survives a notification whose message details failed to load; `messageInfo` is left to
  // supply the forum name and the sender for display.
  //
  // A forum that is a work agreement's or a proposal's conversation opens on that page instead, with
  // the pane open on the forum (#5826). The server names the record on the link itself. Which page
  // of it the reader may open is theirs and changes over time, so the link goes to the page that
  // works it out, and that page falls back to the forum route above.
  const parent = actionLink?.parent;
  if (category === TopicCategory.Messaging.Forum && parent && isConversationRecord(parent.topicCategory)) {
    return conversationDestinationPath({ forumId: id, record: parent.topicCategory, recordId: parent.topicId });
  }

  let actionType: "view" | "edit" | "revise" | "review" = "view";
  let pageTab = "active";
  let tab = category === TopicCategory.Work.Proposal ? "details" : "";

  switch (action) {
    case Action.DraftProposalReview:
      pageTab = "drafts";
      actionType = "review";
      break;
    case Action.DraftProposalReviewApproved:
      pageTab = "drafts";
      actionType = "edit";
      break;
    case Action.DraftProposalSend:
      // A colleague handed the draft over to this leader, who owns it now and sends it (#5841, #6579).
      // Submit lives in the draft editor.
      pageTab = "drafts";
      actionType = "edit";
      break;
    case Action.ProposalReview:
      actionType = "review";
      break;
    case Action.ProposalRevisionReview:
      actionType = "revise";
      if (category === "Work.Agreement") {
        pageTab = "drafts";
      }
      break;
    case Action.Review:
    case Action.ProposalReviewApproved:
    case Action.HireOrCancel:
      actionType = "revise";
      break;
    case Action.ClientTeamLeaderReview:
      // Sent to the client when a proposal reaches them for their own review or hire decision.
      actionType = "review";
      break;
    case Action.ProposalReReviewRequested:
      // Their leader dismissed the approval they had already given and asked for another look,
      // so this opens the review flow exactly as an original review request does.
      actionType = "review";
      break;
    case Action.ProposalReviewerAccessChanged:
      // Their edit rights changed; nobody asked them to review anything, so do not open the
      // review flow.
      actionType = "view";
      break;
    case Action.DraftProposalReviewerAccessChanged:
      // The same change, told about a proposal that has not been submitted (#5119). The active
      // route is the redlining editor, and an unsubmitted proposal has nothing to redline against,
      // so sending them there left them on a loading indicator with nothing left to load.
      pageTab = "drafts";
      actionType = "view";
      break;
    case Action.ProposalReviewerRemoved:
      // A removed reviewer usually loses access to the proposal itself, so the proposal route
      // would bounce them straight back out. Send them to the proposal list they can still open.
      return "/proposals";
  }

  url = url.replace("{pageTab}", pageTab);
  url = url.replace("{action}", actionType);
  url = url.replace("{topicId}", id);
  return url.replace("{tab}", tab);
}

/**
 * Whether clicking this notification takes its recipient anywhere.
 *
 * A category the `notificationLinks` map has no route for has no destination, so the interfaces
 * that list notifications render it as a plain list item rather than as a control that looks
 * clickable and then does nothing. Networking connection announcements are the case this was added
 * for (#4849).
 */
export function isNotificationNavigable(notification: NotificationType | null | undefined): boolean {
  return NotificationLink(notification).length > 0;
}

export function NotificationNavigationState(
  notification: NotificationType,
  returnTo: string,
): NotificationRouteState | undefined {
  if (notification.messageInfo) return undefined;
  if (notification.topic?.category !== TopicCategory.Work.Proposal) return undefined;

  return {
    fromNotification: true,
    returnTo,
  };
}
